/* eslint-disable @angular-eslint/prefer-inject */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Injector,
  OnInit,
  signal,
  computed,
  inject,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
} from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { TabsModule } from "primeng/tabs";
import { InputTextModule } from "primeng/inputtext";
import { DatePickerModule } from "primeng/datepicker";
import { SelectModule } from "primeng/select";
import { CheckboxModule } from "primeng/checkbox";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { CustomProgressComponent } from "../../../shared/components/custom-progress/custom-progress.component";
import { BaseClass } from "../../../shared/class/BaseClass";
import { assets } from "../../../core/constants/assets.const";
import { ImageUploadService } from "../../../service/uploadImage.service";
import {
  BankDetails,
  Documents,
  updateProfile,
  UploadResponse,
} from "../../../models/auth.model";
import { IconField } from "primeng/iconfield";
import { IntlTelInputDirective } from "../../../core/directive/IntlTelInputDirective";
import { TranslateModule } from "@ngx-translate/core";
import { DynamicDialogRef, DialogService } from "primeng/dynamicdialog";
import { VerifyModalComponent } from "../../../dialogs/verify-modal/verify-modal.component";
import { REGEX } from "../../../core/constants/regex.const";
import { emailErrors } from "../../../core/constants/commonErrors.const";
import { keyMessage } from "../../../models/common";
import { FullNameOnlyDirective } from "../../../core/directive/fullNameDirective";
import { appNoSpaceAtFirst } from "../../../core/directive/no-space.directive";
import { RentalPriceOnlyDirective } from "../../../core/directive/priceOnlyDirective";

export interface Option {
  _id: string;
  name: string;
}
export interface DocAPIItem {
  maxSizeMb?: number | undefined;
  mimeTypes?: string[] | undefined;
  _id?: string;
  name?: string;
  issueDate?: boolean;
  expiryDate?: boolean;
  frontImage?: boolean;
  backImage?: boolean;
}
export interface DocumentDetail {
  mimeTypes?: string[];
  maxSizeMb?: number;
  issueDate?: boolean;
  expiryDate?: boolean;
  frontImage?: boolean;
  backImage?: boolean;
}

export const LIST_TYPE = {
  DOCUMENTS: 1,
  SUBJECTS: 2,
  QUALIFICATIONS: 3,
  DEPARTMENTS: 4,
  INDUSTRY: 5,
} as const;

@Component({
  selector: "notchmark-tutor-profile-setup",
  standalone: true,
  imports: [
    CommonModule,
    FullNameOnlyDirective,
    TranslateModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TabsModule,
    InputTextModule,
    DatePickerModule,
    SelectModule,
    CheckboxModule,
    CustomProgressComponent,
    IconField,
    IntlTelInputDirective,
    appNoSpaceAtFirst,
    RentalPriceOnlyDirective,
  ],
  templateUrl: "./tutor-profile-setup.component.html",
  styleUrl: "./tutor-profile-setup.component.scss",
})
export class TutorProfileSetupComponent extends BaseClass implements OnInit {
  @ViewChild(IntlTelInputDirective) telInputDirective!: IntlTelInputDirective;
  // @ViewChild('phone') phoneField!: ElementRef | any;
  @ViewChild("tutorPhoneRef") tutorPhoneEl?: ElementRef<HTMLInputElement>;
  currentStepNumber = computed(() => this.activeTab + 1);
  assetsConst = assets;
  activeTab = 0;
  totalSteps = 4;
  isPhoneVerified = false;
  isEmailVerified = false;
  qualifications = signal<Option[]>([]);
  subjects = signal<Option[]>([]);
  documents = signal<Option[]>([]);
  imageUrl = signal<string>("");
  certificateUrl = signal<string>("");
  frontImage = signal<string>("");
  backImage = signal<string>("");
  submitting = signal(false);
  private imageUploadService = inject(ImageUploadService);
  docDetail = signal<DocumentDetail | null>(null);
  accepts = computed(() => (this.docDetail()?.mimeTypes ?? []).join(","));
  maxBytes = computed(() => (this.docDetail()?.maxSizeMb ?? 2) * 1024 * 1024);
  step1Form!: FormGroup;
  step2Form!: FormGroup;
  step3Form!: FormGroup;
  step4Form!: FormGroup;
  stepCompleted = signal<boolean[]>([false, false, false, false]);
  completedCount = computed(() => this.stepCompleted().filter(Boolean).length);
  maxDob!: Date;
  maxDate!: Date;
  defaultDate!: Date;
  today: Date = new Date();
minExpiryDate: Date = new Date();
  tomorrow: Date = new Date();
  ref: DynamicDialogRef | undefined;
  private dialogService = inject(DialogService);
  protected emailErrors = emailErrors;

  constructor(injector: Injector, private fb: FormBuilder) {
    super(injector);
  }
eighteenYearsAgo!: Date;
ngOnInit(): void {
  // Set tomorrow as minimum expiry date initially
  this.tomorrow.setDate(this.today.getDate() + 1);
  this.minExpiryDate = new Date(this.tomorrow);

  // Initialize forms
  this.initForms();

  // Load dropdown options (qualifications, subjects, documents)
  this.loadLists();

  // Prefill email/phone from signup if available
  this.prefillFromSignup();

  // Resume profile data from server or query params
  this.resumeFromServerOrQuery();

  // Set maximum DOB to 18 years ago AND default opening date
  this.eighteenYearsAgo = new Date();
  this.eighteenYearsAgo.setFullYear(this.eighteenYearsAgo.getFullYear() - 18);
  this.maxDob = this.eighteenYearsAgo;
  this.step1Form.patchValue({ dob: this.eighteenYearsAgo });

  // Restore step data from localStorage if available
  this.restoreStepFromLocalStorage(this.activeTab);

  // Subscribe to form status changes for each step
  this.step1Form.statusChanges
    .pipe(takeUntilDestroyed(this.destroyRef$))
    .subscribe(() => this.updateStepCompletion(0, this.step1Form.valid));

  this.step2Form.statusChanges
    .pipe(takeUntilDestroyed(this.destroyRef$))
    .subscribe(() => this.updateStepCompletion(1, this.step2Form.valid));

  this.step3Form.statusChanges
    .pipe(takeUntilDestroyed(this.destroyRef$))
    .subscribe(() => this.updateStepCompletion(2, this.step3Form.valid));

  this.step4Form.statusChanges
    .pipe(takeUntilDestroyed(this.destroyRef$))
    .subscribe(() => this.updateStepCompletion(3, this.step4Form.valid));

  // Watch for issue date changes to update minExpiryDate dynamically
  this.step4Form.get('issueDate')?.valueChanges
    .pipe(takeUntilDestroyed(this.destroyRef$))
    .subscribe((issueDate: Date | null) => {
      if (issueDate) {
        // Set minExpiryDate to one day after issue date
        this.minExpiryDate = new Date(issueDate);
        this.minExpiryDate.setDate(this.minExpiryDate.getDate() + 1);

        // Clear expiry date if it's before the new minimum
        const expiryDate = this.step4Form.get('expiryDate')?.value;
        if (expiryDate && new Date(expiryDate) <= new Date(issueDate)) {
          this.step4Form.patchValue({ expiryDate: null }, { emitEvent: false });
        }
      } else {
        // Reset to tomorrow if issue date is cleared
        this.minExpiryDate = new Date();
        this.minExpiryDate.setDate(new Date().getDate() + 1);
      }
    });
}


  private initForms() {
    this.step1Form = this.fb.group({
      avatar: [null, [Validators.required]],
      fullName: ["", [Validators.required, Validators.minLength(2)]],
      dob: [null, [Validators.required]],
      phone: ["", [Validators.required, Validators.pattern(/^[0-9]{10,15}$/)]],
      countryCode: [""],
      email: [
        "",
        [
          Validators.required,
          Validators.pattern(REGEX.EMAIL),
          Validators.maxLength(80),
        ],
      ],
    });

    this.step2Form = this.fb.group({
      qualification: [null, Validators.required], // Option
      experience: ["", [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      subjects: this.fb.array([], Validators.required),
      certificate: [null], // string URL
    });

    this.step3Form = this.fb.group({
      bankName: ["", Validators.required],
      accountHolder: ["", Validators.required],
      branchName: ["", Validators.required],
      bankCode: ["", Validators.required],
      accountNumber: ["", Validators.required],
    });

    this.step4Form = this.fb.group({
      documentType: [null, Validators.required],
      issueDate: [null],
      expiryDate: [null],
      frontImage: [null],
      backImage: [null],
    });
  }

  private prefillFromSignup(): void {
    const raw = localStorage.getItem("signupPayload");
    if (!raw) return;

    let payload: any;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    if (!payload || typeof payload !== "object") return;

    const hasEmail =
      typeof payload.email === "string" && payload.email.trim().length > 0;
    const hasPhone =
      typeof payload.phone === "string" && payload.phone.trim().length > 0;
    const fg = this.step1Form;
    if (!fg) return;
    if (hasEmail) {
      const emailCtrl = fg.get("email");
      if (emailCtrl) {
        const was = emailCtrl.disabled;
        if (was) emailCtrl.enable({ emitEvent: false });
        emailCtrl.patchValue(payload.email.trim(), { emitEvent: false });
        emailCtrl.disable({ emitEvent: false });
        this.isEmailVerified = true;
      }
    }
    if (hasPhone) {
      const phoneCtrl = fg.get("phone");
      const ccCtrl = fg.get("countryCode");
      if (!phoneCtrl) return;
      const ccRaw = (payload.countryCode ?? "").toString();
      const phoneRaw = (payload.phone ?? "").toString().trim();
      const phoneWasDisabled = phoneCtrl.disabled;
      if (phoneWasDisabled) phoneCtrl.enable({ emitEvent: false });

      if (ccCtrl) {
        const ccWas = ccCtrl.disabled;
        if (ccWas) ccCtrl.enable({ emitEvent: false });
        ccCtrl.patchValue(ccRaw, { emitEvent: false });
        if (ccWas) ccCtrl.disable({ emitEvent: false });
      }
      phoneCtrl.patchValue(phoneRaw, { emitEvent: false });
      const inputEl = this.tutorPhoneEl?.nativeElement;
      setTimeout(() => {
        const telDir = this.telInputDirective as any;
        if (telDir && typeof telDir.setCountryFromPhoneNumber === "function") {
          const full = `${ccRaw}${phoneRaw}`;
          if (full) telDir.setCountryFromPhoneNumber(full);
        } else if (telDir && typeof telDir.writeValue === "function") {
          telDir.writeValue(phoneRaw);
        }
        if (inputEl) {
          inputEl.value = phoneRaw;
          inputEl.dispatchEvent(new Event("input", { bubbles: true }));
        }
        phoneCtrl.updateValueAndValidity({ emitEvent: false });
        phoneCtrl.disable({ emitEvent: false });
        this.isPhoneVerified = true;
      }, 0);
    }
    if (!hasEmail) fg.get("email")?.enable({ emitEvent: false });
    if (!hasPhone) fg.get("phone")?.enable({ emitEvent: false });
  }

  getEmailError(): keyMessage | undefined {
    const control = this.step1Form.controls["email"];
    return this.emailErrors.find((err) => control.hasError(err.key));
  }

  openVerify(kind: "email" | "phone") {
    const data: any = { otpType: 4 };
    const form = this.step1Form;
    if (kind === "email") {
      const email = form.get("email")?.value?.trim();
      if (!email) return;
      data.email = email;
    } else {
      const phone = form.get("phone")?.value?.trim();
      const cc = form.get("countryCode")?.value;
      if (!phone) return;
      data.phone = phone;
      data.countryCode = cc;
    }
    this._authService.sendOtpToUpdate(data).subscribe({
      next: () => {
        this.ref = this.dialogService.open(VerifyModalComponent, {
          header: "Verify " + (kind === "email" ? "Email" : "Phone"),
          width: "32rem",
          dismissableMask: true,

          data,
        });
        this.ref?.onClose.subscribe(
          (result?: { verified: boolean; server?: any }) => {
            if (result?.verified && result.server)
              this.handleVerifySuccess(result.server);
          }
        );
      },
      error: () => {
        // Optional: show toast for send OTP failure
      },
    });
  }
  handleVerifySuccess(res: any) {
    const data = res?.data ?? res;
    if (!data) return;
    if (typeof data.isEmailVerified === "boolean")
      this.isEmailVerified = data.isEmailVerified;
    if (typeof data.isPhoneVerified === "boolean")
      this.isPhoneVerified = data.isPhoneVerified;
  }

  private loadLists() {
    this._authService
      .getSettingsData(`type=${LIST_TYPE.QUALIFICATIONS}`)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((res: any[]) => this.qualifications.set(this.toOptions(res)));

    this._authService
      .getSettingsData(`type=${LIST_TYPE.SUBJECTS}`)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((res: any[]) => this.subjects.set(this.toOptions(res)));

    this._authService
      .getSettingsData(`type=${LIST_TYPE.DOCUMENTS}`)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((res: any[]) => this.documents.set(this.toOptions(res)));
  }

  private toOptions(arr: any[]): Option[] {
    return (arr ?? []).map((x) => ({
      _id: x._id ?? x.code ?? x.value,
      name: x.name ?? x.label ?? String(x),
    }));
  }

  private resumeFromServerOrQuery() {
    const qpStep = Number(this.route.snapshot.queryParamMap.get("step") ?? "");
    if (!Number.isNaN(qpStep) && qpStep >= 0 && qpStep <= 3) {
      this.activeTab = qpStep;
    }
    this._authService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe((draft: any) => {
        if (!draft) return;
        if (draft.fullName)
          this.step1Form.patchValue({ fullName: draft.fullName });
        if (draft.dob) this.step1Form.patchValue({ dob: new Date(draft.dob) });
        if (draft.image) {
          this.imageUrl.set(draft.image);
          this.step1Form.patchValue({ avatar: draft.image });
        }
        if (draft.phone) this.step1Form.patchValue({ phone: draft.phone });
        if (draft.email) this.step1Form.patchValue({ email: draft.email });

        if (draft.qualificationId) {
          const qual =
            this.qualifications().find(
              (q) => q._id === draft.qualificationId
            ) ?? null;
          this.step2Form.patchValue({ qualification: qual });
        }
        if (Array.isArray(draft.subjectIds)) {
          const mapped = this.subjects().filter((s) =>
            draft.subjectIds.includes(s._id)
          );
          mapped.forEach((m) => this.subjectsArray.push(this.fb.control(m)));
        }
        if (draft.experience != null)
          this.step2Form.patchValue({ experience: String(draft.experience) });
        if (draft.certificate) {
          this.certificateUrl.set(draft.certificate);
          this.step2Form.patchValue({ certificate: draft.certificate });
        }

        if (draft.bankDetails) {
          this.step3Form.patchValue({
            bankName: draft.bankDetails.bankName ?? "",
            accountHolder: draft.bankDetails.accountHolderName ?? "",
            branchName: draft.bankDetails.branchName ?? "",
            bankCode: draft.bankDetails.bankCode ?? "",
            accountNumber: draft.bankDetails.accountNumber ?? "",
          });
        }

        if (draft.documents) {
          const docOpt =
            this.documents().find(
              (d) => d._id === draft.documents.documentId
            ) ?? null;
          this.step4Form.patchValue({ documentType: docOpt });
          if (docOpt) this.onDocumentTypeChange(docOpt);
          if (draft.documents.issueDate)
            this.step4Form.patchValue({
              issueDate: draft.documents.issueDate.slice(0, 10),
            });
          if (draft.documents.expiryDate)
            this.step4Form.patchValue({
              expiryDate: draft.documents.expiryDate.slice(0, 10),
            });
          if (draft.documents.frontImage) {
            this.frontImage.set(draft.documents.frontImage);
            this.step4Form.patchValue({
              frontImage: draft.documents.frontImage,
            });
          }
          if (draft.documents.backImage) {
            this.backImage.set(draft.documents.backImage);
            this.step4Form.patchValue({ backImage: draft.documents.backImage });
          }
        }
        if (typeof draft.profileSteps === "number") {
          this.activeTab = Math.min(draft.profileSteps, 3);
        }
      });
  }

  // subjects
  get subjectsArray(): FormArray {
    return this.step2Form.get("subjects") as FormArray;
  }
  isSubjectSelected(subj: Option): boolean {
    return (this.subjectsArray.value as Option[]).some(
      (s) => s?._id === subj._id
    );
  }
  onSubjectChange(subj: Option, checked: boolean) {
    const idx = (this.subjectsArray.value as Option[]).findIndex(
      (s) => s?._id === subj._id
    );
    if (checked && idx === -1) this.subjectsArray.push(this.fb.control(subj));
    if (!checked && idx > -1) this.subjectsArray.removeAt(idx);
    this.subjectsArray.updateValueAndValidity();
  }

  onDocumentTypeChange(opt: Option | null) {
    this.docDetail.set(null);
    this.step4Form.patchValue({
      issueDate: null,
      expiryDate: null,
      frontImage: null,
      backImage: null,
    });
    this.minExpiryDate = new Date();
  this.minExpiryDate.setDate(this.today.getDate() + 1);
    if (opt?._id) {
      this._authService
        .getSettingsData(`type=${LIST_TYPE.DOCUMENTS}&documentId=${opt._id}`)
        .pipe(takeUntilDestroyed(this.destroyRef$))
        .subscribe((resp: any) => {
          const item: DocAPIItem | null = resp?.length ? resp[0] : null;
          if (!item) {
            this.docDetail.set(null);
            return;
          }
          const detail: DocumentDetail = {
            issueDate: item.issueDate,
            expiryDate: item.expiryDate,
            frontImage: item.frontImage,
            backImage: item.backImage,
          };
          this.docDetail.set(detail);
          const form = this.step4Form;
          ["issueDate", "expiryDate", "frontImage", "backImage"].forEach(
            (k) => {
              const c = form.get(k);
              if (!c) return;
              c.clearValidators();
              c.updateValueAndValidity({ emitEvent: false });
            }
          );
          if (detail.issueDate)
            form.get("issueDate")?.addValidators(Validators.required);
          if (detail.expiryDate)
            form.get("expiryDate")?.addValidators(Validators.required);
          if (detail.frontImage)
            form.get("frontImage")?.addValidators(Validators.required);
          if (detail.backImage)
            form.get("backImage")?.addValidators(Validators.required);
          form.get("issueDate")?.updateValueAndValidity({ emitEvent: false });
          form.get("expiryDate")?.updateValueAndValidity({ emitEvent: false });
          form.get("frontImage")?.updateValueAndValidity({ emitEvent: false });
          form.get("backImage")?.updateValueAndValidity({ emitEvent: false });
        });
    }
  }
  get avatarCtrl() {
    return this.step1Form.get("avatar");
  }

  async onAvatarSelected(event: Event, type: "image") {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) return;
    const res: UploadResponse = await this.imageUploadService.onFileChange(
      event,
      type
    );
    const url = res?.data ?? "";
    this.imageUrl.set(url);
    this.step1Form.patchValue({ avatar: url });
    this.avatarCtrl?.updateValueAndValidity();
    if (input) input.value = "";
  }

  certificateFileType = signal<"image" | "pdf" | null>(null);
  certificateFileName = signal<string>("");

  async onCertificateSelected(event: Event, type: "doc") {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) return;
    this.certificateFileName.set(file.name);
    const ext = file.name.split(".").pop()?.toLowerCase();
    const fileType: "image" | "pdf" = ["jpg", "jpeg", "png"].includes(ext || "")
      ? "image"
      : "pdf";

    const res: UploadResponse = await this.imageUploadService.onFileChange(
      event,
      type
    );
    const url = res?.data ?? "";

    this.certificateUrl.set(url);
    this.certificateFileType.set(fileType);
    this.step2Form.patchValue({ certificate: url });
    this.step2Form.get("certificate")?.updateValueAndValidity();

    if (input) input.value = "";
  }
  removeCertificate() {
    this.certificateUrl.set("");
    this.certificateFileType.set(null);
    this.certificateFileName.set("");
    this.step2Form.patchValue({ certificate: "" });
    this.step2Form.get("certificate")?.updateValueAndValidity();
  }

  frontImageType = signal<"image" | "pdf" | null>(null);
  frontImageFileName = signal<string>("");

  backImageType = signal<"image" | "pdf" | null>(null);
  backImageFileName = signal<string>("");
  async onFrontFileSelected(event: Event, type: "image") {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) return;
    this.frontImageFileName.set(file.name);
    const ext = file.name.split(".").pop()?.toLowerCase();
    const fileType: "image" | "pdf" = ["jpg", "jpeg", "png", "webp"].includes(
      ext || ""
    )
      ? "image"
      : "pdf";

    const res: UploadResponse = await this.imageUploadService.onFileChange(
      event,
      type
    );
    const url = res?.data ?? "";

    this.frontImage.set(url);
    this.frontImageType.set(fileType);
    this.step4Form.patchValue({ frontImage: url });
    this.step4Form.get("frontImage")?.updateValueAndValidity();

    if (input) input.value = "";
  }

  async onBackFileSelected(event: Event, type: "image") {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    if (!file) return;
    this.backImageFileName.set(file.name);
    const ext = file.name.split(".").pop()?.toLowerCase();
    const fileType: "image" | "pdf" = ["jpg", "jpeg", "png", "webp"].includes(
      ext || ""
    )
      ? "image"
      : "pdf";

    const res: UploadResponse = await this.imageUploadService.onFileChange(
      event,
      type
    );
    const url = res?.data ?? "";

    this.backImage.set(url);
    this.backImageType.set(fileType);
    this.step4Form.patchValue({ backImage: url });
    this.step4Form.get("backImage")?.updateValueAndValidity();

    if (input) input.value = "";
  }

  removeFrontImage() {
    this.frontImage.set("");
    this.frontImageType.set(null);
    this.frontImageFileName.set("");
    this.step4Form.patchValue({ frontImage: "" });
    this.step4Form.get("frontImage")?.updateValueAndValidity();
  }

  removeBackImage() {
    this.backImage.set("");
    this.backImageType.set(null);
    this.backImageFileName.set("");
    this.step4Form.patchValue({ backImage: "" });
    this.step4Form.get("backImage")?.updateValueAndValidity();
  }

  phoneChange(event: { isoCode: string; dialCode: string }): void {
    const control = this.step1Form.get("countryCode");
    if (control) {
      control.setValue(event.dialCode);
    }
  }

  private updateStepCompletion(stepIndex: number, isValid: boolean) {
    const arr = [...this.stepCompleted()];
    arr[stepIndex] = isValid;
    this.stepCompleted.set(arr);
  }
  progressValueFor(i: number): number {
    return this.stepCompleted()[i] ? 100 : 0;
  }

  private canGoNext(): boolean {
    switch (this.activeTab) {
      case 0:
        return this.step1Form.valid;
      case 1:
        return this.step2Form.valid;
      case 2:
        return this.step3Form.valid;
      default:
        return false;
    }
  }

async onNextClick() {
  if (this.activeTab < this.totalSteps - 1 && !this.canGoNext()) {
    this.touchCurrentStep();
    return; 
  }
  this.saveStepToLocalStorage(this.activeTab);
  
  const nextStepIndex = Math.min(this.activeTab + 1, this.totalSteps - 1);
  const profileSteps = nextStepIndex;
  await this.saveStep(this.activeTab, profileSteps);

  if (this.activeTab < this.totalSteps - 1) {
    this.activeTab = nextStepIndex;
    this.restoreStepFromLocalStorage(this.activeTab);
    this.router.navigate([], {
      queryParams: { step: this.activeTab },
      queryParamsHandling: 'merge',
    });
  } else {
    if (!this.step4Form.valid) {
      this.touchCurrentStep();
      return;
    }
    await this.saveFinal();
    this.clearLocalStorage();
    
    this.router.navigate(['/']);
  }
}

private clearLocalStorage(): void {
  for (let i = 0; i < 4; i++) {
    localStorage.removeItem(`profileStep${i}`);
  }

  localStorage.removeItem('imageUrl');
  localStorage.removeItem('certificateUrl');
  localStorage.removeItem('certificateFileType');
  localStorage.removeItem('certificateFileName');
  localStorage.removeItem('frontImage');
  localStorage.removeItem('backImage');
  localStorage.removeItem('frontImageType');
  localStorage.removeItem('backImageType');
  localStorage.removeItem('frontImageFileName');
  localStorage.removeItem('backImageFileName');
}
private saveStepToLocalStorage(stepIndex: number): void {
  const key = `profileStep${stepIndex}`;
  let formData: any;
  switch(stepIndex) {
    case 0:
      formData = this.step1Form.getRawValue();
      break;
    case 1:
      formData = this.step2Form.getRawValue();
      break;
    case 2:
      formData = this.step3Form.getRawValue();
      break;
    case 3:
      formData = this.step4Form.getRawValue();
      break;
  }

  localStorage.setItem(key, JSON.stringify(formData));
  if (stepIndex === 0) {
    localStorage.setItem('imageUrl', this.imageUrl());
  }
  if (stepIndex === 1) {
    localStorage.setItem('certificateUrl', this.certificateUrl());
    localStorage.setItem('certificateFileType', this.certificateFileType() || '');
    localStorage.setItem('certificateFileName', this.certificateFileName());
  }
  if (stepIndex === 3) {
    localStorage.setItem('frontImage', this.frontImage());
    localStorage.setItem('backImage', this.backImage());
    localStorage.setItem('frontImageType', this.frontImageType() || '');
    localStorage.setItem('backImageType', this.backImageType() || '');
    localStorage.setItem('frontImageFileName', this.frontImageFileName());
    localStorage.setItem('backImageFileName', this.backImageFileName());
  }
}

private restoreStepFromLocalStorage(stepIndex: number): void {
  const key = `profileStep${stepIndex}`;
  const savedData = localStorage.getItem(key);
  if (!savedData) return;
  
  try {
    const formData = JSON.parse(savedData);
    switch(stepIndex) {
      case 0:
        { this.step1Form.patchValue(formData, { emitEvent: false });
        const savedImageUrl = localStorage.getItem('imageUrl');
        if (savedImageUrl) {
          this.imageUrl.set(savedImageUrl);
        }
        break; }
        
      case 1:
        { this.step2Form.patchValue(formData, { emitEvent: false });
        const savedCertUrl = localStorage.getItem('certificateUrl');
        const savedCertType = localStorage.getItem('certificateFileType');
        const savedCertName = localStorage.getItem('certificateFileName');
        
        if (savedCertUrl) this.certificateUrl.set(savedCertUrl);
        if (savedCertType) this.certificateFileType.set(savedCertType as any);
        if (savedCertName) this.certificateFileName.set(savedCertName);
        break; }
        
      case 2:
        this.step3Form.patchValue(formData, { emitEvent: false });
        break;
        
      case 3:
        { this.step4Form.patchValue(formData, { emitEvent: false });
        const savedFront = localStorage.getItem('frontImage');
        const savedBack = localStorage.getItem('backImage');
        const savedFrontType = localStorage.getItem('frontImageType');
        const savedBackType = localStorage.getItem('backImageType');
        const savedFrontName = localStorage.getItem('frontImageFileName');
        const savedBackName = localStorage.getItem('backImageFileName');
        
        if (savedFront) this.frontImage.set(savedFront);
        if (savedBack) this.backImage.set(savedBack);
        if (savedFrontType) this.frontImageType.set(savedFrontType as any);
        if (savedBackType) this.backImageType.set(savedBackType as any);
        if (savedFrontName) this.frontImageFileName.set(savedFrontName);
        if (savedBackName) this.backImageFileName.set(savedBackName);
        break; }
    }
  } catch (error) {
    console.error('Error restoring form data:', error);
  }
}
async onPrevClick() {
  this.saveStepToLocalStorage(this.activeTab);
  this.activeTab = Math.max(this.activeTab - 1, 0);
  this.restoreStepFromLocalStorage(this.activeTab);
  this.router.navigate([], {
    queryParams: { step: this.activeTab },
    queryParamsHandling: 'merge',
  });
}

  private touchCurrentStep() {
    const form = [
      this.step1Form,
      this.step2Form,
      this.step3Form,
      this.step4Form,
    ][this.activeTab];
    form.markAllAsTouched();
  }

  private buildBaseAggregateForUpdate(): updateProfile {
    const qualification = this.step2Form.value.qualification as Option | null;
    const documentType = this.step4Form.value.documentType as Option | null;

    const subjectIds =
      (this.step2Form.value.subjects as Option[] | undefined)?.map(
        (s) => s._id
      ) ?? [];

    const raw: updateProfile & {
      subjectIds?: string[];
      bankDetails?: BankDetails;
      documents?: Documents;
    } = {
      fullName: this.step1Form.value.fullName || undefined,
      dob: this.step1Form.value.dob
        ? new Date(this.step1Form.value.dob).toISOString()
        : undefined,
      image: this.step1Form.value.avatar || undefined,
      email: this.step1Form.value.email || undefined,

      qualificationId: qualification?._id || undefined,
      subjectIds: subjectIds.length ? subjectIds : undefined,
      certificate: this.step2Form.value.certificate || undefined,

      bankDetails: {
        bankName: this.step3Form.value.bankName || undefined,
        accountHolderName: this.step3Form.value.accountHolder || undefined,
        branchName: this.step3Form.value.branchName || undefined,
        bankCode: this.step3Form.value.bankCode || undefined,
        accountNumber:
          (this.step3Form.value.accountNumber ?? "").toString() || undefined,
      },

      documents: {
        documentId: documentType?._id || undefined,
        issueDate: this.step4Form.value.issueDate
          ? new Date(this.step4Form.value.issueDate).toISOString()
          : undefined,
        expiryDate: this.step4Form.value.expiryDate
          ? new Date(this.step4Form.value.expiryDate).toISOString()
          : undefined,
        frontImage: this.step4Form.value.frontImage || undefined,
        backImage: this.step4Form.value.backImage || undefined,
      },
    };
    const pruned = this.prune(raw) as updateProfile;
    return pruned;
  }

  omitUndefined<T>(obj: T): T {
    if (obj === null || typeof obj !== "object") return obj;
    const acc: any = Array.isArray(obj) ? [] : {};
    for (const [k, v] of Object.entries(obj as any)) {
      if (v === undefined) continue;
      acc[k] = this.omitUndefined(v as any);
    }
    return acc;
  }
  prune(obj: any): any {
    if (obj === null || obj === undefined) return undefined;
    if (Array.isArray(obj)) {
      const arr = obj.map(this.prune).filter((v) => v !== undefined);
      return arr.length ? arr : undefined;
    }
    if (typeof obj === "object") {
      const out: any = {};
      for (const [k, v] of Object.entries(obj)) {
        const pv = this.prune(v);
        if (pv !== undefined) out[k] = pv;
      }
      return Object.keys(out).length ? out : undefined;
    }
    return obj;
  }

  private async saveStep(currentStepIndex: number, profileStepsIndex: number) {
    const agg = this.buildBaseAggregateForUpdate();
    const payload = this.prune({
      ...agg,
      profileSteps: Math.min(profileStepsIndex + 1, 4),
      isProfileComplete: false,
    }) as updateProfile;
    this.submitting.set(true);
    this._authService
      .updateProfile(payload)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          this._authService.profileDetails.next(res);
        },
      });
  }

  saveFinal() {
    const agg = this.buildBaseAggregateForUpdate();
    const payload = this.prune({
      ...agg,
      profileSteps: 4,
      isProfileComplete: true,
    }) as updateProfile;
    this._authService
      .updateProfile(payload)
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: (res) => {
          this._authService.profileDetails.next(res);
        },
      });
  }

}
